// src/services/firestore/facilityTypeService.js

import { getApp } from 'firebase/app'
import {
  getFirestore, collection, addDoc, getDocs, query, orderBy,
  updateDoc, doc, serverTimestamp
} from 'firebase/firestore'

/* Resolve Firestore without importing your local firebase.js */
const db = (() => {
  try { return getFirestore(getApp()) } catch { return getFirestore() }
})()

/* Small helper to read the signed-in session (for audit fields) */
function readSession() {
  try { return JSON.parse(localStorage.getItem('session') || 'null') || {} } catch { return {} }
}

/**
 * Fetch all facility types (active + inactive), ordered by name.
 * Returns: [{ id, name, isActive, createdAt, createdByEmail, createdByName, deletedAt, deletedByEmail, deletedByName }]
 */
export async function refreshFacilityTypes() {
  const snap = await getDocs(query(collection(db, 'facilityTypes'), orderBy('name')))
  return snap.docs.map(d => {
    const data = d.data() || {}
    return {
      id: d.id,
      name: data.name || '',
      isActive: data.isActive !== false,
      createdAt: data.createdAt || null,
      createdByEmail: data.createdByEmail || '',
      createdByName: data.createdByName || '',
      deletedAt: data.deletedAt || null,
      deletedByEmail: data.deletedByEmail || '',
      deletedByName: data.deletedByName || '',
      restoredAt: data.restoredAt || null,
      restoredByEmail: data.restoredByEmail || '',
      restoredByName: data.restoredByName || '',
    }
  })
}

/**
 * Add a new facility type (active by default).
 */
export async function addFacilityType(name) {
  const label = String(name || '').trim()
  if (!label) throw new Error('Type name is required.')

  const sess = readSession()
  const whoEmail = sess.email || ''
  const whoName  = sess.displayName || whoEmail || 'user'

  await addDoc(collection(db, 'facilityTypes'), {
    name: label,
    isActive: true,
    createdAt: serverTimestamp(),
    createdByEmail: whoEmail,
    createdByName: whoName,
    // clear any delete/restoration markers
    deletedAt: null,
    deletedByEmail: '',
    deletedByName: '',
    restoredAt: null,
    restoredByEmail: '',
    restoredByName: '',
  })
}

/**
 * Soft delete (archive) a facility type by id.
 */
export async function softDeleteFacilityType(id) {
  const sess = readSession()
  const whoEmail = sess.email || ''
  const whoName  = sess.displayName || whoEmail || 'user'

  await updateDoc(doc(db, 'facilityTypes', id), {
    isActive: false,
    deletedAt: serverTimestamp(),
    deletedByEmail: whoEmail,
    deletedByName: whoName,
  })
}

/**
 * Restore an archived facility type by id.
 */
export async function restoreFacilityType(id) {
  const sess = readSession()
  const whoEmail = sess.email || ''
  const whoName  = sess.displayName || whoEmail || 'user'

  await updateDoc(doc(db, 'facilityTypes', id), {
    isActive: true,
    deletedAt: null,
    deletedByEmail: '',
    deletedByName: '',
    restoredAt: serverTimestamp(),
    restoredByEmail: whoEmail,
    restoredByName: whoName,
  })
}